import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Grid,
  CircularProgress,
  Typography,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/ToastContext";
import {api}from "../services/api";
import {PropTypes} from "prop-types";
 
// Utility to interpolate URLs
const interpolateUrl = (template, formData, region) => {
  if (!template) return "";
  return template.replaceAll(/\{(\w+)\}/g, (_, key) =>
    key === "location" ? region || "" : formData[key] || ""
  );
};
 
const DynamicDiskForm = ({
  formId = 59,
  correlationId = "8f7d862b-5995-401f-a514-7f9f1f9ebae5",
  resourceOverview,resourceTypeId
}) => {
  const toast = useToast();
  const [priceDetails, setPriceDetails] = useState(null);
  const [formConfig, setFormConfig] = useState([]);
  const [disk, setDisk] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
 const organizationName = sessionStorage.getItem("organizationName") || "Organization";
  const locationObj = resourceOverview?.resourceRequestDetails?.find?.(
    (item) => item.fieldName === "location"
  );
  const region = locationObj ? locationObj.fieldValue : "";
 
 
 
 
  const initializeFormState = (config) => {
    const initial = {};
    config.forEach((f) => {
      initial[f.fieldName] = "";
    });
    setDisk(initial);
    setInitialized(true);
  };

  // Fetch config ONLY when formId/correlationId changes, and only initialize form once
  useEffect(() => {
    setLoading(true);
    api
      .get(`/api/Resources/form/${formId}/${correlationId}`, {
        headers: {
          accept: "application/json",
          "X-CORRELATION-ID": correlationId,
        },
      })
      .then((res) => {
        const config = Array.isArray(res.data) ? res.data : [];
        setFormConfig(config);
        if (!initialized) {
          initializeFormState(config);
        }
        setErrors({});
      })
      .catch(() => {
        setFormConfig([]);
        setErrors({});
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [formId, correlationId, initialized]);
 
  // Reset initialized if formId/correlationId change
  useEffect(() => {
    setInitialized(false);
  }, [formId, correlationId]);
 
  // ========== EXTRACTED HELPER FUNCTIONS TO REDUCE NESTING ==========
  
  const getParentFields = (parentFieldString) => {
    return parentFieldString
      ? parentFieldString.split(",").map((p) => p.trim())
      : [];
  };

  const checkParentFieldsFilled = (parentFields) => {
    return parentFields.every((parent) => {
      if (parent === "location") return region;
      return disk[parent];
    });
  };

  const shouldFetchDropdown = (field, fieldName, url) => {
    const isApiDropdown = field.fieldType === "dropdown" && 
                          field.dropdownType === "api" && 
                          field.dropdownSource;
    if (!isApiDropdown) return false;
    
    const hasNoOptions = !dropdownOptions[fieldName];
    const urlChanged = dropdownOptions[fieldName + "_url"] !== url;
    return hasNoOptions || urlChanged;
  };

  const setDropdownLoadingState = (fieldName, isLoading) => {
    setDropdownLoading((prev) => ({
      ...prev,
      [fieldName]: isLoading,
    }));
  };

  const updateDropdownOptions = (fieldName, data, url) => {
    setDropdownOptions((prev) => ({
      ...prev,
      [fieldName]: data,
      [fieldName + "_url"]: url,
    }));
  };

  const fetchFieldDropdown = (field) => {
    const parentFields = getParentFields(field.parentField);
    const parentsFilled = checkParentFieldsFilled(parentFields);
    const url = interpolateUrl(field.dropdownSource, disk, region);

    if (!parentsFilled || !shouldFetchDropdown(field, field.fieldName, url)) {
      return;
    }

    setDropdownLoadingState(field.fieldName, true);

    api
      .get(url)
      .then((res) => updateDropdownOptions(field.fieldName, res.data, url))
      .catch(() => updateDropdownOptions(field.fieldName, [], url))
      .finally(() => setDropdownLoadingState(field.fieldName, false));
  };

  // Fetch dropdown options when parent fields are filled
  useEffect(() => {
    if (!formConfig.length) return;
    formConfig.forEach(fetchFieldDropdown);
  }, [formConfig, disk, region]);
 
  // Fetch price details
  const fetchPriceDetails = async () => {
    const organizationId = sessionStorage.getItem("organizationId");
    const cloudProviderId = "1";
    const vmSizeId = "0";
    const diskSizeId = disk.DiskOption;
    
    const url = `/api/AzureResource/vmPrice/${organizationId}/${cloudProviderId}`;
    const config = {
      headers: {
        accept: "text/plain",
        "X-CORRELATION-ID": "ae6b75ca-6251-49b0-b33e-9bfaa5b6b612",
      },
      params: { vmSizeId, diskSizeId },
    };
    
    try {
      const response = await api.get(url, config);
      setPriceDetails(response.data);
    } catch {
      toast.error("Failed to fetch price details.");
    }
  };
 
  useEffect(() => {
    if (disk.DiskOption) {
      fetchPriceDetails();
    }
  }, [disk.DiskOption]);
 
  // Handle input change
  const validateFieldValue = (fieldName, value) => {
    const field = formConfig.find((f) => f.fieldName === fieldName);
    if (field?.isRequired && !value) return "This field is required";
    if (fieldName === "DiskSize" && value) return validateDiskSize(value, fieldName);
    return null;
  };

  const handleDiskChange = (name, value) => {
    if (name === "DiskSize") {
      value = value.replaceAll(/\D/g, "");
      if (value.length > 4) value = value.slice(0, 4);
    }
    setDisk((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "diskType" ? { redundency: "" } : {}),
    }));
    
    const error = validateFieldValue(name, value);
    setErrors((prev) => {
      const updated = { ...prev };
      if (error) {
        updated[name] = error;
      } else {
        delete updated[name];
      }
      return updated;
    });
  };
 
  const validateDiskSize = (val, fieldName) => {
    if (!val) return null;
    if (!/^\d{1,4}$/.test(val)) return "Enter a valid number (max 4 digits)";
    const numVal = Number(val);
    if (numVal > 2048) return "Maximum allowed is 2048";
    if (numVal < 1) return "Minimum allowed is 1";
    return null;
  };

  const validate = () => {
    const newErrors = {};
    formConfig.forEach((field) => {
      const val = disk[field.fieldName];
      if (field.isRequired && !val) {
        newErrors[field.fieldName] = "This field is required";
        return;
      }
      if (field.fieldName === "DiskSize") {
        const sizeError = validateDiskSize(val, field.fieldName);
        if (sizeError) newErrors[field.fieldName] = sizeError;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
 
  const getDiskSizeHelper = (fieldLabel, isRequired, fieldName) => {
    const fieldRequired = isRequired ? " " : "";
    const hasError = errors[fieldName];
    const helperMessage = hasError ? errors[fieldName] : "Maximum can enter only 2048 GiB (2TB)";
    
    return (
      <TextField
        key={fieldName}
        label={fieldLabel + fieldRequired}
        value={disk[fieldName] || ""}
        onChange={(e) => handleDiskChange(fieldName, e.target.value)}
        required={isRequired}
        fullWidth
        margin="normal"
        sx={{ minWidth: 220 }}
        type="text"
        error={!!hasError}
        helperText={helperMessage}
          slotProps={{
    htmlInput: { 
      inputMode: "numeric", 
      pattern: "[0-9]*", 
      maxLength: 4 
    },
    input: { 
      endAdornment: <InputAdornment position="end">GiB</InputAdornment> 
    } }}
      />
    );
  };

  const getOptionValue = (opt, field) => {
    if (typeof opt !== "object") return { value: opt, label: opt };
    return {
      value: opt[field.valueField] ?? opt.value,
      label: opt[field.labelField] ?? opt.label ?? opt.text,
    };
  };

  const getDropdownOptions = (field) => {
    const rawOptions = field.dropdownType === "api"
      ? dropdownOptions[field.fieldName] || []
      : field.dropdownValues || [];
    
    if (!rawOptions || !Array.isArray(rawOptions)) return [];
    return rawOptions.map((opt) => getOptionValue(opt, field));
  };

  const renderTextfield = (field) => {
    if (field.fieldName === "DiskSize") {
      return getDiskSizeHelper(field.fieldLabel, field.isRequired, field.fieldName);
    }
    
    const fieldRequired = field.isRequired ? " " : "";
    return (
      <TextField
        key={field.fieldName}
        label={field.fieldLabel + fieldRequired}
        value={disk[field.fieldName] || ""}
        onChange={(e) => handleDiskChange(field.fieldName, e.target.value)}
        required={field.isRequired}
        fullWidth
        margin="normal"
        sx={{ minWidth: 220 }}
        type="text"
        error={!!errors[field.fieldName]}
        helperText={errors[field.fieldName] || ""}
      />
    );
  };

  const renderDropdownMenuItems = (options, isLoading) => {
    if (isLoading) {
      return (
        <MenuItem disabled>
          <CircularProgress size={18} /> Loading...
        </MenuItem>
      );
    }
    if (options.length === 0) {
      return <MenuItem disabled>No options</MenuItem>;
    }
    return options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ));
  };

  const renderDropdownfield = (field) => {
    const options = getDropdownOptions(field);
    const isLoading = field.dropdownType === "api" && dropdownLoading[field.fieldName];
    const fieldRequired = field.isRequired ? " *" : "";
    const hasError = !!errors[field.fieldName];

    return (
      <FormControl key={field.fieldName} fullWidth margin="normal" error={hasError}>
        <InputLabel>{field.fieldLabel + fieldRequired}</InputLabel>
        <Select
          value={disk[field.fieldName] || ""}
          label={field.fieldLabel + fieldRequired}
          onChange={(e) => handleDiskChange(field.fieldName, e.target.value)}
          required={field.isRequired}
          sx={{ minWidth: 220 }}
          disabled={isLoading}
        >
          {renderDropdownMenuItems(options, isLoading)}
        </Select>
        {hasError && (
          <Typography color="error" variant="caption">
            {errors[field.fieldName]}
          </Typography>
        )}
      </FormControl>
    );
  };

  const renderField = (field) => {
    if (field.fieldType === "text") return renderTextfield(field);
    if (field.fieldType === "dropdown") return renderDropdownfield(field);
    return null;
  };
 
const getDiskTypeText = () => {
  const diskTypeField = formConfig.find((field) => field.fieldName === "diskType");
  
  if (diskTypeField?.fieldType !== "dropdown") return disk.diskType;
  
  const rawOptions = diskTypeField.dropdownType === "api"
    ? dropdownOptions[diskTypeField.fieldName] || []
    : diskTypeField.dropdownValues || [];
  
  const getOptionValue = (opt) => {
    return typeof opt === "object" 
      ? (opt[diskTypeField?.valueField] ?? opt.value) 
      : opt;
  };
  
  const getOptionLabel = (opt) => {
    if (typeof opt !== "object") return opt;
    return opt[diskTypeField?.labelField] ?? opt.label ?? opt.text;
  };
  
  const selectedOption = rawOptions.find((opt) => getOptionValue(opt) === disk.diskType);
  
  return selectedOption ? getOptionLabel(selectedOption) : disk.diskType;
};

const buildPayload = () => {
  const cloudProvider = localStorage.getItem("selectedProviderId");
  const subscription = localStorage.getItem("selectedSubscription") || "";
  const requester = sessionStorage.getItem("userId") ;
  const parentResourceID = resourceOverview?.resource_Id || "";
  const totalPrice = priceDetails?.discountPricePerMonth || "";
  const marketPrice = priceDetails.marketRatePerMonth.toFixed(2);
  const diskTypeText = getDiskTypeText();
  
  const disksPayload = [formConfig.reduce((acc, field) => {
    const val = field.fieldName === "diskType" ? diskTypeText : disk[field.fieldName];
    if (val !== undefined && val !== "") acc[field.fieldName] = val;
    return acc;
  }, {})];
  
  return {
    applicationName: resourceOverview?.applicationName || "",
    subscription,
    totalPrice: totalPrice.toString(),
    resourceRequest: JSON.stringify({ disks: disksPayload }),
    requester,
    resourceType: resourceTypeId || 1,
    organizationId: Number.parseInt(sessionStorage.getItem("organizationId")),
    Parent_Resource_ID: Number.parseInt(parentResourceID) || 0,
    region: region,
    marketPrice,
    cloudProvider,
    operationId: 59,
    operationName: "Additional Disk"
  };
};

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;
  
  try {
    const payload = buildPayload();
    await api.post("/api/Resources/createOperationalRequest", payload, {
      headers: { "Content-Type": "application/json" },
    });
    toast.success("Resource created successfully.");
    navigate("/provision-status");
  } catch (error) {
    toast.error(error.message || "Failed to create resource.");
    handleReset();
    navigate('/Resources/View_Resources');
  }
};
 
const handleReset = () => {
  initializeFormState(formConfig);
  setErrors({});
  setPriceDetails(null);
};
 
return (
  <Box
    component="form"
    sx={{ maxWidth: 'auto', mx: "auto", mt: 3 }}
    onSubmit={handleSubmit}
  >
    {loading ? (
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress />
      </Box>
    ) : (
      <>
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            background: "#fafafa",
          }}
        >
          <Grid container spacing={2}>
            {formConfig.map((field) => (
              <Grid item xs={12} md={6} size={6} key={field.fieldName}>
                {renderField(field)}
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box mt={2} display="flex" gap={70}>
          <Button variant="cancelContained" onClick={handleReset}>
            Cancel
          </Button>
          <Button
            variant="greenContained"
            type="submit"
            color="success"
          >
            Submit
          </Button>
        </Box>
        {priceDetails && (
          <>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Market Rate Per Month: ${priceDetails.marketRatePerMonth.toFixed(2)}
            </Typography>
            <Typography variant="h5" sx={{ mt: 2, color: '#2b61aa', fontWeight: 'bold' }}>
              {organizationName} Rate Per Month: ${priceDetails.discountPricePerMonth.toFixed(2)}
            </Typography>
          </>
        )}
      </>
    )}
  </Box>
);

};
DynamicDiskForm.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  correlationId: PropTypes.string,
  resourceTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resourceOverview: PropTypes.shape({
    applicationName: PropTypes.string,
    resource_Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resourceRequestDetails: PropTypes.arrayOf(
      PropTypes.shape({
        fieldName: PropTypes.string,
        fieldValue: PropTypes.any,
      })
    ),
  }),
};
 
export default DynamicDiskForm;